// "Vendor Emails — Needs Review": forwarded vendor emails the matcher could not apply on its own.
// The PM picks the PO; apply goes through the same path as an auto-match (ship date / ETA / tracking → PO + WO note).
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Mail, Pencil, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { btn } from "@/components/common/btn";
import { shortDate } from "@/lib/format";
import { useAuth } from "@/auth/AuthContext";
import {
  listPurchasingInbox, applyInboxToPO, dismissPurchasingInbox, reprocessPurchasingInbox,
  getPurchasingInboxSettings, putPurchasingInboxSettings,
} from "@/lib/api";

const ACTION = { acknowledge: "Acknowledgement", ship_notice: "Ship Notice", delay: "Delay", quote: "Quote", invoice: "Invoice", other: "Unclassified" };
const WHY = { pending_parse: "Parsing…", matched_pending: "Matched, low confidence", flagged: "No PO match" };

const guessPO = (row, pos) => {
  if (row.matched_po_id) return row.matched_po_id;
  const n = (row.classification?.po_number_raw || row.classification?.po_number || "").toLowerCase();
  if (!n) return "";
  const hit = pos.find((p) => (p.po_number || "").toLowerCase() === n) || pos.find((p) => (p.po_number || "").toLowerCase().endsWith(n));
  return hit?.id || "";
};

function Facts({ c }) {
  if (!c) return null;
  const chips = [
    c.po_number_raw || c.po_number ? `PO ${c.po_number_raw || c.po_number}` : null,
    c.vendor_order_number ? `Order # ${c.vendor_order_number}` : null,
    c.ship_date ? `Ships ${shortDate(c.ship_date)}` : null,
    c.eta ? `ETA ${shortDate(c.eta)}` : null,
    c.tracking_number ? `${c.carrier ? `${c.carrier} ` : ""}${c.tracking_number}` : null,
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">{ACTION[c.action] || c.action || "—"}</span>
      {chips.map((t) => <span key={t} className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-800 num">{t}</span>)}
    </div>
  );
}

function ReviewRow({ row, pos, canWrite, onDone }) {
  const [poId, setPoId] = useState(() => guessPO(row, pos));
  const [busy, setBusy] = useState(false);
  const c = row.classification;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-2 border-t border-slate-200 px-3 py-2 text-xs lg:grid-cols-[minmax(0,1fr)_auto]" data-testid={`vendor-email-row-${row.id}`}>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-slate-500 num whitespace-nowrap">{row.received_at ? new Date(row.received_at).toLocaleString() : "—"}</span>
          <span className="font-medium text-slate-800 truncate">{c?.original_from || row.from_name || row.from_email}</span>
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-testid={`vendor-email-why-${row.id}`}>{WHY[row.status] || row.status}</span>
        </div>
        <div className="truncate text-slate-700" title={row.subject}>{row.subject || "(no subject)"}</div>
        <Facts c={c} />
        {(c?.vendor_message || row.applied_action_summary) && (
          <div className="truncate text-slate-500" title={c?.vendor_message || row.applied_action_summary}>{c?.vendor_message || row.applied_action_summary}</div>
        )}
      </div>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-1.5 lg:justify-end">
          <select value={poId} onChange={(e) => setPoId(e.target.value)} className="h-7 max-w-[260px] rounded-md border border-slate-200 bg-white px-1.5 text-xs" data-testid={`vendor-email-po-select-${row.id}`}>
            <option value="">Pick the PO…</option>
            {pos.map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.manufacturer_name || "—"}{p.wo_number ? ` · ${p.wo_number}` : ""}</option>)}
          </select>
          <button type="button" className={btn("write")} disabled={busy || !poId}
            onClick={() => run(() => applyInboxToPO(row.id, poId), (r) => r.data?.match?.summary || "Applied to PO")} data-testid={`vendor-email-apply-${row.id}`}>Apply</button>
          <button type="button" className={btn("nav")} disabled={busy} title="Re-parse the email" onClick={() => run(() => reprocessPurchasingInbox(row.id), "Re-parsed")} data-testid={`vendor-email-reparse-${row.id}`}>
            <RefreshCw className="h-3 w-3" />
          </button>
          <button type="button" className={`${btn("nav")} text-red-600`} disabled={busy} title="Dismiss — not about a PO" onClick={() => run(() => dismissPurchasingInbox(row.id), "Dismissed")} data-testid={`vendor-email-dismiss-${row.id}`}>
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function InboundAddress({ canEdit }) {
  const [addr, setAddr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { getPurchasingInboxSettings().then((r) => setAddr(r.data?.inbound_address || "")).catch(() => setAddr("")); }, []);
  if (addr === null) return null;
  const save = async () => {
    try { const r = await putPurchasingInboxSettings(draft.trim()); setAddr(r.data?.inbound_address || ""); setEditing(false); toast.success("Forwarding address saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="purchasing@absinc.com" className="h-7 w-56 text-xs" data-testid="vendor-email-address-input" />
        <button type="button" className={btn("write")} onClick={save} data-testid="vendor-email-address-save">Save</button>
        <button type="button" className={btn("nav")} onClick={() => setEditing(false)}>Cancel</button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="vendor-email-address">
      Forward vendor emails to {addr ? <span className="font-mono text-slate-700">{addr}</span> : <span className="italic">— address not set</span>}
      {canEdit && (
        <button type="button" className="text-slate-400 hover:text-slate-700" title="Edit forwarding address" onClick={(e) => { e.stopPropagation(); setDraft(addr); setEditing(true); }} data-testid="vendor-email-address-edit">
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

export default function VendorEmailReviewPanel({ pos = [], onApplied }) {
  const { can, user } = useAuth();
  const canWrite = can("ops.purchasing", "write");
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try { const r = await listPurchasingInbox({ status: "needs_review", limit: 50 }); setRows(r.data?.rows || []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickable = useMemo(() => pos.filter((p) => !["received", "closed", "cancelled"].includes(p.status)).sort((a, b) => (a.po_number || "").localeCompare(b.po_number || "")), [pos]);
  const done = () => { load(); onApplied?.(); };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="vendor-email-review-panel">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button type="button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800" onClick={() => setOpen((o) => !o)} data-testid="vendor-email-review-toggle">
          {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <Mail className="h-4 w-4 text-slate-400" /> Vendor Emails
          <span className={`rounded-full px-2 py-0.5 text-[10px] num ${rows.length ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`} data-testid="vendor-email-review-count">{rows.length} to review</span>
        </button>
        <button type="button" className="text-slate-400 hover:text-slate-700" title="Refresh" onClick={load} data-testid="vendor-email-review-refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
        <div className="ml-auto"><InboundAddress canEdit={isAdmin} /></div>
      </div>
      {open && rows.length > 0 && rows.map((row) => <ReviewRow key={row.id} row={row} pos={pickable} canWrite={canWrite} onDone={done} />)}
      {open && rows.length === 0 && (
        <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-400" data-testid="vendor-email-review-empty">
          Nothing waiting. Forwarded acknowledgements and ship notices update the PO (ship date, ETA, tracking) and post a note on the work order automatically.
        </div>
      )}
    </div>
  );
}
